import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Spin, Table, Tabs, Tag } from 'antd';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import moment from 'moment';

import FilterSearchBar from 'components/shared/FilterSearchBar';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormRangePicker from 'components/shared/BWFormControl/FormDateRange';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import { getGrandstreamVoipReportSummary } from 'services/voip-report.services';
import { getGrandstreamExtensionAssignments } from 'services/voip.services';
import { DIRECTION_CALL, STATUS_CALL } from 'pages/CDRs/utils/constants';
import { showToast } from 'utils/helpers';

ChartJS.register(...registerables);

const Page = styled.div`
  .voip_report_header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .voip_report_title {
    color: #111827;
    font-size: 24px;
    font-weight: 800;
    margin: 0;
  }

  .voip_report_meta {
    color: #64748b;
    font-size: 13px;
    margin-top: 4px;
  }

  .voip_report_grid {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-bottom: 16px;
  }

  .voip_report_card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
    padding: 16px;
  }

  .voip_report_kpi_label {
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
  }

  .voip_report_kpi_value {
    color: #0f172a;
    font-size: 26px;
    font-weight: 800;
    line-height: 34px;
    margin-top: 6px;
  }

  .voip_report_kpi_sub {
    color: #64748b;
    font-size: 12px;
    margin-top: 4px;
  }

  .voip_report_chart_grid {
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 2fr) minmax(360px, 1fr);
    margin-bottom: 16px;
  }

  .voip_report_card_title {
    color: #111827;
    font-size: 16px;
    font-weight: 800;
    margin-bottom: 12px;
  }

  .voip_report_chart {
    height: 320px;
  }

  .voip_report_table {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 16px;
    padding: 16px;
  }

  @media (max-width: 1200px) {
    .voip_report_grid,
    .voip_report_chart_grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .voip_report_grid,
    .voip_report_chart_grid {
      grid-template-columns: 1fr;
    }
  }
`;

const MISSED_OPTIONS = [
  { label: 'Tất cả', value: '' },
  { label: 'Chỉ cuộc gọi nhỡ/lỗi', value: 1 },
  { label: 'Chỉ cuộc gọi nghe máy', value: 2 },
];

const defaultFilters = {
  direction: '',
  end_date: null,
  extension: [],
  is_missed: '',
  phone_search: '',
  start_date: null,
  status: '',
};

const EMPTY_ARRAY = [];

const formatNumber = (value) => new Intl.NumberFormat('vi-VN').format(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatSeconds = (value) => {
  const totalSeconds = Math.round(Number(value || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes}p ${seconds}s` : `${seconds}s`;
};

const normalizeSelectArray = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => (item && typeof item === 'object' ? item.value || item.id : item))
    .filter((item) => item !== undefined && item !== null && item !== '');

const normalizeFilters = (values = {}) => ({
  ...values,
  extension: normalizeSelectArray(values.extension),
  is_missed: values.is_missed || '',
  phone: values.phone_search || '',
});

const isMeaningful = (items = []) => items.some((item) => Number(item.total || item.answered || item.missed || 0) > 0);

const emptyOrChart = (items, chart) => {
  if (!isMeaningful(items)) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Chưa có dữ liệu' />;
  return chart;
};

const VoipReportPage = () => {
  const methods = useForm({ defaultValues: defaultFilters });
  const [filters, setFilters] = useState(defaultFilters);
  const [report, setReport] = useState({ advanced: {}, overview: {} });
  const [extensionOptions, setExtensionOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadExtensions = useCallback(() => {
    getGrandstreamExtensionAssignments({ itemsPerPage: 20 })
      .then((result) => {
        const options = (result?.extensions || []).map((item) => ({
          label: `${item.extension}${item.fullname ? ` - ${item.fullname}` : ''}${item.status ? ` (${item.status})` : ''}`,
          value: item.extension,
        }));
        setExtensionOptions(options);
      })
      .catch(() => setExtensionOptions([]));
  }, []);

  const loadReport = useCallback(() => {
    setLoading(true);
    getGrandstreamVoipReportSummary(filters)
      .then(setReport)
      .catch((error) => showToast.error(error?.message || 'Không tải được báo cáo tổng đài'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(loadExtensions, [loadExtensions]);
  useEffect(loadReport, [loadReport]);

  const overview = report?.overview || {};
  const advanced = report?.advanced || {};
  const {
    by_agent: byAgent = EMPTY_ARRAY,
    by_day: byDay = EMPTY_ARRAY,
    by_direction: byDirection = EMPTY_ARRAY,
    by_hour: byHour = EMPTY_ARRAY,
    by_status: byStatus = EMPTY_ARRAY,
    failed_calls: failedCalls = EMPTY_ARRAY,
    top_missed_numbers: topMissedNumbers = EMPTY_ARRAY,
  } = advanced;

  const trendChartData = useMemo(
    () => ({
      labels: byDay.map((item) => (item.date === 'Không rõ' ? item.date : moment(item.date).format('DD/MM'))),
      datasets: [
        {
          label: 'Tổng cuộc gọi',
          data: byDay.map((item) => item.total || 0),
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.12)',
          fill: true,
          tension: 0.35,
        },
        {
          label: 'Nghe máy',
          data: byDay.map((item) => item.answered || 0),
          borderColor: '#16a34a',
          backgroundColor: 'rgba(22, 163, 74, 0.12)',
          fill: true,
          tension: 0.35,
        },
        {
          label: 'Nhỡ/lỗi',
          data: byDay.map((item) => item.missed || 0),
          borderColor: '#dc2626',
          backgroundColor: 'rgba(220, 38, 38, 0.08)',
          fill: true,
          tension: 0.35,
        },
      ],
    }),
    [byDay],
  );

  const statusChartData = useMemo(
    () => ({
      labels: byStatus.map((item) => item.status),
      datasets: [
        {
          data: byStatus.map((item) => item.total || 0),
          backgroundColor: ['#2563eb', '#16a34a', '#f97316', '#dc2626', '#7c3aed', '#64748b', '#06b6d4'],
          borderWidth: 0,
        },
      ],
    }),
    [byStatus],
  );

  const hourChartData = useMemo(
    () => ({
      labels: byHour.map((item) => item.hour),
      datasets: [
        {
          label: 'Tổng cuộc gọi',
          data: byHour.map((item) => item.total || 0),
          backgroundColor: '#0ea5e9',
          borderRadius: 4,
        },
      ],
    }),
    [byHour],
  );

  const directionChartData = useMemo(
    () => ({
      labels: byDirection.map((item) => {
        if (item.direction === 'inbound') return 'Gọi vào';
        if (item.direction === 'outbound') return 'Gọi ra';
        if (item.direction === 'local') return 'Nội bộ';
        return item.direction;
      }),
      datasets: [
        {
          label: 'Cuộc gọi',
          data: byDirection.map((item) => item.total || 0),
          backgroundColor: ['#2563eb', '#16a34a', '#f97316', '#64748b'],
          borderRadius: 4,
        },
      ],
    }),
    [byDirection],
  );

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { boxWidth: 12, color: '#334155' },
      },
    },
    responsive: true,
  };

  const agentColumns = [
    {
      dataIndex: 'extension',
      title: 'Máy nhánh',
      width: 120,
      render: (value) => <Tag color={value ? 'blue' : 'default'}>{value || 'Chưa gán'}</Tag>,
    },
    {
      dataIndex: 'user_ext',
      title: 'Nhân viên',
      width: 240,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'total',
      title: 'Tổng',
      align: 'right',
      sorter: (a, b) => Number(a.total || 0) - Number(b.total || 0),
    },
    {
      dataIndex: 'answered',
      title: 'Nghe máy',
      align: 'right',
    },
    {
      dataIndex: 'missed',
      title: 'Nhỡ/lỗi',
      align: 'right',
      render: (value) => <span style={{ color: value ? '#dc2626' : '#334155', fontWeight: 700 }}>{formatNumber(value)}</span>,
    },
    {
      dataIndex: 'answer_rate',
      title: 'Answer rate',
      align: 'right',
      render: formatPercent,
      sorter: (a, b) => Number(a.answer_rate || 0) - Number(b.answer_rate || 0),
    },
    {
      dataIndex: 'avg_billsec',
      title: 'Talk TB',
      align: 'right',
      render: formatSeconds,
    },
    {
      dataIndex: 'billsec',
      title: 'Talk time',
      align: 'right',
      render: formatSeconds,
    },
  ];

  const missedColumns = [
    { dataIndex: 'number', title: 'Số điện thoại', width: 180 },
    { dataIndex: 'total', title: 'Số lần nhỡ/lỗi', align: 'right', sorter: (a, b) => Number(a.total || 0) - Number(b.total || 0) },
    { dataIndex: 'last_status', title: 'Trạng thái cuối', render: (value) => <Tag color='error'>{value || '-'}</Tag> },
    { dataIndex: 'last_time', title: 'Lần cuối' },
  ];

  const failedColumns = [
    { dataIndex: 'time_started', title: 'Thời gian', width: 180 },
    { dataIndex: 'extension', title: 'Máy nhánh', width: 110 },
    { dataIndex: 'user_ext', title: 'Nhân viên', width: 240, render: (value) => value || '-' },
    { dataIndex: 'direction', title: 'Hướng', width: 100 },
    { dataIndex: 'from_number', title: 'Từ số' },
    { dataIndex: 'to_number', title: 'Đến số' },
    { dataIndex: 'status', title: 'Trạng thái', render: (value) => <Tag color='error'>{value}</Tag> },
    { dataIndex: 'cause', title: 'Cause' },
  ];

  const handleSubmit = (values) => {
    setFilters(normalizeFilters(values));
  };

  const handleClear = () => {
    methods.reset(defaultFilters);
    setFilters(defaultFilters);
  };

  return (
    <Page className='bw_main_wrapp'>
      <div className='voip_report_header'>
        <div>
          <h2 className='voip_report_title'>Báo cáo VoIP Grandstream</h2>
          <div className='voip_report_meta'>Cập nhật: {report?.generated_at || '-'}</div>
        </div>
        <Button type='primary' onClick={loadReport} loading={loading}>
          Tải lại
        </Button>
      </div>

      <FormProvider {...methods}>
        <FilterSearchBar
          title='Bộ lọc báo cáo'
          expanded
          colSize={3}
          onSubmit={handleSubmit}
          onClear={handleClear}
          actions={[
            {
              title: 'Thời gian gọi',
              component: (
                <FormRangePicker
                  fieldStart='start_date'
                  fieldEnd='end_date'
                  placeholder={['Từ ngày', 'Đến ngày']}
                  format='DD/MM/YYYY'
                  allowClear
                />
              ),
            },
            {
              title: 'Máy nhánh',
              component: <FormSelect field='extension' list={extensionOptions} mode='multiple' allowClear />,
            },
            {
              title: 'Hướng cuộc gọi',
              component: <FormSelect field='direction' list={[{ label: 'Tất cả', value: '' }, ...DIRECTION_CALL]} allowClear />,
            },
            {
              title: 'Trạng thái',
              component: <FormSelect field='status' list={[{ label: 'Tất cả', value: '' }, ...STATUS_CALL]} allowClear />,
            },
            {
              title: 'Nhóm cuộc gọi',
              component: <FormSelect field='is_missed' list={MISSED_OPTIONS} allowClear />,
            },
            {
              title: 'Số điện thoại',
              component: <FormInput field='phone_search' placeholder='Nhập số điện thoại' />,
            },
          ]}
        />
      </FormProvider>

      <Spin spinning={loading}>
        <div className='voip_report_grid'>
          <div className='voip_report_card'>
            <div className='voip_report_kpi_label'>Tổng cuộc gọi</div>
            <div className='voip_report_kpi_value'>{formatNumber(overview.total)}</div>
            <div className='voip_report_kpi_sub'>Nguồn CDR: {formatNumber(overview.source_records)}</div>
          </div>
          <div className='voip_report_card'>
            <div className='voip_report_kpi_label'>Answer rate</div>
            <div className='voip_report_kpi_value'>{formatPercent(overview.answer_rate)}</div>
            <div className='voip_report_kpi_sub'>{formatNumber(overview.answered)} cuộc nghe máy</div>
          </div>
          <div className='voip_report_card'>
            <div className='voip_report_kpi_label'>Missed / lỗi</div>
            <div className='voip_report_kpi_value' style={{ color: '#dc2626' }}>
              {formatNumber(overview.missed)}
            </div>
            <div className='voip_report_kpi_sub'>{formatPercent(overview.missed_rate)} tổng cuộc gọi</div>
          </div>
          <div className='voip_report_card'>
            <div className='voip_report_kpi_label'>Talk time TB</div>
            <div className='voip_report_kpi_value'>{formatSeconds(overview.avg_talk_seconds)}</div>
            <div className='voip_report_kpi_sub'>Tổng đàm thoại {formatSeconds(overview.billsec)}</div>
          </div>
        </div>

        <Tabs
          defaultActiveKey='standard'
          items={[
            {
              key: 'standard',
              label: 'Báo cáo chuẩn',
              children: (
                <>
                  <div className='voip_report_chart_grid'>
                    <div className='voip_report_card'>
                      <div className='voip_report_card_title'>Xu hướng theo ngày</div>
                      <div className='voip_report_chart'>
                        {emptyOrChart(byDay, <Chart type='line' data={trendChartData} options={chartOptions} />)}
                      </div>
                    </div>
                    <div className='voip_report_card'>
                      <div className='voip_report_card_title'>Phân bổ trạng thái</div>
                      <div className='voip_report_chart'>
                        {emptyOrChart(byStatus, <Chart type='doughnut' data={statusChartData} options={chartOptions} />)}
                      </div>
                    </div>
                  </div>

                  <div className='voip_report_table'>
                    <div className='voip_report_card_title'>Hiệu suất theo nhân viên / máy nhánh</div>
                    <Table
                      bordered
                      columns={agentColumns}
                      dataSource={byAgent}
                      pagination={{ pageSize: 10, showSizeChanger: true }}
                      rowKey={(row) => row.extension || row.user_ext || 'unassigned'}
                      scroll={{ x: 1000 }}
                    />
                  </div>
                </>
              ),
            },
            {
              key: 'advanced',
              label: 'Báo cáo chuyên sâu',
              children: (
                <>
                  <div className='voip_report_chart_grid'>
                    <div className='voip_report_card'>
                      <div className='voip_report_card_title'>Phân bổ theo giờ</div>
                      <div className='voip_report_chart'>
                        {emptyOrChart(byHour, <Chart type='bar' data={hourChartData} options={chartOptions} />)}
                      </div>
                    </div>
                    <div className='voip_report_card'>
                      <div className='voip_report_card_title'>Hướng cuộc gọi</div>
                      <div className='voip_report_chart'>
                        {emptyOrChart(byDirection, <Chart type='bar' data={directionChartData} options={chartOptions} />)}
                      </div>
                    </div>
                  </div>

                  <div className='voip_report_table'>
                    <div className='voip_report_card_title'>Top số điện thoại nhỡ / lỗi</div>
                    <Table
                      bordered
                      columns={missedColumns}
                      dataSource={topMissedNumbers}
                      pagination={{ pageSize: 10 }}
                      rowKey='number'
                    />
                  </div>

                  <div className='voip_report_table'>
                    <div className='voip_report_card_title'>Cuộc gọi lỗi gần nhất</div>
                    <Table
                      bordered
                      columns={failedColumns}
                      dataSource={failedCalls}
                      pagination={{ pageSize: 10 }}
                      rowKey={(row, index) => `${row.time_started}_${row.extension}_${index}`}
                      scroll={{ x: 1200 }}
                    />
                  </div>
                </>
              ),
            },
          ]}
        />
      </Spin>
    </Page>
  );
};

export default VoipReportPage;
